import type { FC, ReactNode } from 'react';
import { Suspense } from 'react';
import { useRouter } from 'next/router';

import { Box, Divider, ErrorBoundary, Stack, useDeviceType } from '@packages/shared';

import { usePaging } from '../../hooks/usePaging';
import { useDisplayMode } from '../../hooks/useDisplayMode';
import type { SearchApiResponse } from '../../api/rest/types';
import { DisplaySettings } from '../DisplaySettings';
import { InfiniteProductGrid } from '../InfiniteProductGrid';
import { ProductGrid } from '../ProductGrid';
import { ProductListPagination } from '../ProductListPagination';
import type { FilterSettingsProps } from '../FilterSettings';
import { FilterSettings } from '../FilterSettings';
import { SearchAdServerIntegration } from '../SearchAdServerIntegration';
import { extractTrackingInformation } from '../../utils/extractTrackingInformation';

export type ProductListPageLayoutProps = {
  apiResponse: SearchApiResponse;
  topLevelCategories?: string[];
  aboveMainContentComponent: ReactNode;
  leftSidebarDesktopComponent: ReactNode;
  renderCategoryDialogContent: FilterSettingsProps['renderCategoryDialogContent'];
  belowProductsComponent: ReactNode;
  pageType: 'serp' | 'productlist';
};

/**
 * Shared layout for pages displaying product lists
 *
 * Includes the actual product grid, filtering, sorting, display mode, pagination, and slots for additional content.
 * */
export const ProductListPageLayout: FC<ProductListPageLayoutProps> = ({
  apiResponse,
  topLevelCategories,
  aboveMainContentComponent,
  leftSidebarDesktopComponent,
  renderCategoryDialogContent,
  belowProductsComponent,
  pageType,
}) => {
  const { isMobile } = useDeviceType();

  const {
    filters: availableFilters = [],
    selectedFilters = [],
    orders: availableSortingOrders = [],
  } = apiResponse.searchresult.result;

  const { pageCount, displayType: apiDisplayType } = apiResponse.searchresult.information;

  const { asPath } = useRouter();
  const { pagingMode } = usePaging();
  const { displayMode } = useDisplayMode(apiDisplayType);

  const { requestModification } = apiResponse.searchresult.information;

  const criteoData = {
    criteoResponse: apiResponse.searchresult.result.criteo,
    criteoTrackingProps: {
      category: apiResponse.searchresult.request.category,
      current: apiResponse.searchresult.result.navigation.current,
      orders: apiResponse.searchresult.result.orders,
      query: apiResponse.searchresult.request.query,
    },
  };

  const categoryDataForTracking = {
    breadcrumbData: apiResponse.searchresult.result.navigation.breadCrumb,
    currentCategoryName: apiResponse.searchresult.result.navigation.current?.name,
  };

  const productGridProps = {
    topLevelCategories,
    displayMode,
    categoryDataForTracking,
    criteoData,
    selectedFilters,
    pageType,
  };

  return (
    // position relative is necessary for the SearchAdServerIntegration component
    <Stack gap={1} padding={1} position="relative">
      {aboveMainContentComponent}

      <FilterSettings
        availableFilters={availableFilters}
        selectedFilters={selectedFilters}
        availableSortingOrders={availableSortingOrders}
        showRemovedFiltersNotification={requestModification?.removedFilters}
        renderCategoryDialogContent={renderCategoryDialogContent}
      />

      <DisplaySettings
        availableSortingOrders={availableSortingOrders}
        apiDisplayType={apiDisplayType}
        pageCount={pageCount}
      />

      <Divider />

      <Stack direction="row" gap={1}>
        {!isMobile && (
          <Box sx={{ width: { md: '25%', lg: '20%' } }}>{leftSidebarDesktopComponent}</Box>
        )}

        <Stack
          gap={2}
          sx={{
            flex: 1,
            // NOTE: minWidth is required to properly propagate size constraints to RecoSlider and prevent the page from blowing up
            // see also: https://github.com/philipwalton/flexbugs#1-minimum-content-sizing-of-flex-items-not-honored
            minWidth: 0,
          }}
        >
          {pagingMode === 'infinite' ? (
            <InfiniteProductGrid
              key={asPath}
              {...productGridProps}
              {...extractTrackingInformation(apiResponse)}
            />
          ) : (
            <ProductGrid
              key={asPath}
              {...productGridProps}
              {...extractTrackingInformation(apiResponse)}
            />
          )}
          {pageCount > 1 && pagingMode === 'limited' && (
            <Box
              sx={{
                alignSelf: 'center',
              }}
            >
              <ProductListPagination pageCount={pageCount} />
            </Box>
          )}
          {belowProductsComponent}
        </Stack>
      </Stack>

      <ErrorBoundary>
        <Suspense>
          <SearchAdServerIntegration majorMkz={apiResponse?.searchresult?.result?.majorMkz} />
        </Suspense>
      </ErrorBoundary>
    </Stack>
  );
};
